// Browser WebAuthn helpers. Server-issued challenges are always verified by the API.
const LS_CRED_KEY = "icard_passkey_cred_id";

function b64urlDecode(value) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

function b64urlEncode(buffer) {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function prepareCreationOptions(options) {
  return {
    ...options,
    challenge: b64urlDecode(options.challenge),
    user: { ...options.user, id: b64urlDecode(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((item) => ({ ...item, id: b64urlDecode(item.id) })),
  };
}

function prepareRequestOptions(options) {
  return {
    ...options,
    challenge: b64urlDecode(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((item) => ({ ...item, id: b64urlDecode(item.id) })),
  };
}

function credentialToJSON(credential) {
  const response = credential.response;
  const data = {
    id: credential.id,
    rawId: b64urlEncode(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: b64urlEncode(response.clientDataJSON),
    },
  };
  if (response.attestationObject) data.response.attestationObject = b64urlEncode(response.attestationObject);
  if (response.authenticatorData) data.response.authenticatorData = b64urlEncode(response.authenticatorData);
  if (response.signature) data.response.signature = b64urlEncode(response.signature);
  if (response.userHandle) data.response.userHandle = b64urlEncode(response.userHandle);
  if (typeof response.getTransports === "function") data.response.transports = response.getTransports();
  return data;
}

export function isPasskeySupported() {
  return !!(window.isSecureContext && window.PublicKeyCredential && navigator.credentials?.create && navigator.credentials?.get);
}

/** Start registration with a fresh server challenge and return a verifiable response. */
export async function enrollPasskey(_user, api) {
  if (!isPasskeySupported()) throw new Error("Passkeys need a secure browser and a device that supports them");
  const { data: ceremony } = await api.post("/auth/passkey/register/options");
  const credential = await navigator.credentials.create({ publicKey: prepareCreationOptions(ceremony.public_key) });
  if (!credential) throw new Error("Passkey setup was cancelled");
  return { challengeId: ceremony.challenge_id, credential: credentialToJSON(credential) };
}

/** Start discoverable passkey sign-in and let the server verify the assertion. */
export async function authenticatePasskey(api) {
  if (!isPasskeySupported()) throw new Error("Passkeys need a secure browser and a device that supports them");
  const { data: ceremony } = await api.post("/auth/passkey/login/options");
  const credential = await navigator.credentials.get({ publicKey: prepareRequestOptions(ceremony.public_key) });
  if (!credential) throw new Error("Passkey sign-in was cancelled");
  const { data } = await api.post("/auth/passkey/login/verify", {
    challenge_id: ceremony.challenge_id,
    credential: credentialToJSON(credential),
  });
  localStorage.setItem(LS_CRED_KEY, credential.id);
  return data;
}

export function rememberPasskey(credentialId) {
  if (credentialId) localStorage.setItem(LS_CRED_KEY, credentialId);
}

export function clearStoredPasskey(credentialId) {
  if (!credentialId || localStorage.getItem(LS_CRED_KEY) === credentialId) {
    localStorage.removeItem(LS_CRED_KEY);
  }
}
